/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import NetworkPicker from "../../../components/esim/zones/NetworkPicker";
import ZoneStyleFields from "../../../components/esim/zones/ZoneStyleFields";
import AlertBox from "../../../components/ui/Alert";
import Button from "../../../components/ui/Button";
import FormWrapper from "../../../components/ui/FormWrapper";
import { WithTip } from "../../../components/ui/InfoTip";
import Input from "../../../components/ui/input/Input";
import Modal from "../../../components/ui/Modal";
import Select from "../../../components/ui/select/Select";
import { SpinLoader } from "../../../components/ui/SpinLoader";
import useFetch from "../../../hooks/useFetch";
import {
  EsimZoneSaveOutcome,
  IEsimZoneForm,
  IEsimZoneSaveResult
} from "../../../interfaces/esim/EsimZone.types";
import {
  createEsimZone,
  getEsimZone,
  getEsimZoneNetworkOptions,
  updateEsimZone
} from "../../../services/EsimZone.service";
import { getAllCountries } from "../../../services/Locations.service";
import EsimZoneSchema from "../../../utils/validations/EsimZone.schema";

type Mode = "create" | "edit";

const CRUMBS = ["Esim", "Zonat"];

const TITLES: Record<Mode, string> = {
  create: "Zonë e re",
  edit: "Ndrysho zonën"
};

const DEFAULTS: IEsimZoneForm = {
  nameSq: "",
  nameEn: "",
  countries: [],
  isRegional: false,
  highRisk: false,
  blockedForPurchase: false,
  imageUrl: "",
  coverImageUrl: ""
};

const FIXED =
  "Shtetet dhe rrjetet e zonës janë në OCS, që nuk lejon ndryshimin e zonës; " +
  "për rrjete të tjera krijo zonë të re. Adresa në web mbetet e njëjtë.";

const CONFIRM =
  "Zona krijohet në OCS dhe nuk mund të fshihet më nga paneli. Kontrollo emrin, shtetet dhe rrjetet.";

const toggled = (set: Set<number>, id: number): Set<number> => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

// One page for creating a zone (in OCS, then here), editing what lives here, and importing a zone that
// exists only in OCS; built from the shared form pieces like every other create/edit page.
const EsimZoneForm: FC<{ mode: Mode }> = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const methods = useForm<IEsimZoneForm>({
    // Same shim as the other forms: yup's transforms widen optionals to null.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(EsimZoneSchema) as any,
    defaultValues: DEFAULTS
  });
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, dirtyFields }
  } = methods;

  const zone = useFetch(getEsimZone, { initRequest: mode === "edit", params: Number(id) });

  const countries = watch("countries");
  const countryIds = useMemo(() => countries.map((c) => c.id), [countries]);
  const options = useFetch(getEsimZoneNetworkOptions, {
    initRequest: countryIds.length > 0,
    params: countryIds
  });
  const networkOptions = (options.response?.data?.countries ?? []).filter((c) =>
    countryIds.includes(c.countryId)
  );

  const [unticked, setUnticked] = useState<Set<number>>(new Set());
  const [refused, setRefused] = useState<Map<string, string>>(new Map());
  const [result, setResult] = useState<IEsimZoneSaveResult | null>(null);
  const [pending, setPending] = useState<IEsimZoneForm | null>(null);
  const [creating, setCreating] = useState(false);
  const inFlight = useRef(false);
  // Not isSubmitSuccessful: in create mode a submit only opens the confirmation, and saves nothing.
  const [saved, setSaved] = useState(false);
  // What the form last ticked by itself; null once the admin has chosen otherwise.
  const autoRegional = useRef<boolean | null>(DEFAULTS.isRegional);

  useEffect(() => {
    const data = zone.response?.data;
    if (mode !== "edit" || !data?.id) return;
    reset({
      ...DEFAULTS,
      nameSq: data.nameSq ?? "",
      nameEn: data.nameEn ?? "",
      isRegional: data.isRegional,
      highRisk: data.highRisk,
      blockedForPurchase: data.blockedForPurchase,
      imageUrl: data.imageUrl ?? "",
      coverImageUrl: data.coverImageUrl ?? ""
    });
  }, [zone.response, mode, reset]);

  // A zone of several countries is regional unless the admin says otherwise. Unticking returns the field
  // to its default, so dirtyFields cannot tell; a value other than the one set here can.
  useEffect(() => {
    if (mode !== "create" || autoRegional.current === null) return;
    if (getValues("isRegional") !== autoRegional.current) {
      autoRegional.current = null;
      return;
    }
    autoRegional.current = countries.length > 1;
    setValue("isRegional", autoRegional.current);
  }, [countries.length, mode]);

  const tadigs = networkOptions.flatMap((c) =>
    c.networks.filter((n) => !unticked.has(n.networkId)).flatMap((n) => n.tadigs)
  );
  const uncovered = networkOptions.filter((c) => !c.networks.some((n) => !unticked.has(n.networkId)));
  const networksReady =
    !options.loading &&
    countries.length > 0 &&
    networkOptions.length === countries.length &&
    uncovered.length === 0;

  const style = (data: IEsimZoneForm) => ({
    nameSq: data.nameSq,
    nameEn: data.nameEn,
    isRegional: data.isRegional,
    highRisk: data.highRisk,
    imageUrl: data.imageUrl ?? "",
    coverImageUrl: data.coverImageUrl ?? ""
  });

  const done = (answer: IEsimZoneSaveResult, message: string) => {
    setResult(answer);
    if (answer.outcome !== EsimZoneSaveOutcome.Saved) return;
    toast.success(message);
    setSaved(true);
    // A new zone has no packages yet: adding them is the next step. Deferred like the other forms, so the
    // unsaved-changes prompt is off before the router is asked to leave.
    setTimeout(() => {
      if (mode !== "edit" && answer.slug) navigate(`/esim/packages?zone=${encodeURIComponent(answer.slug)}`);
      else navigate("/esim/zones", { state: CRUMBS });
    }, 500);
  };

  // Runs from the confirmation, outside handleSubmit, so it handles its own failure; the zone cannot be
  // deleted from OCS once made, so a second click must not send a second create.
  const create = async (data: IEsimZoneForm) => {
    // A ref, not the state: two clicks in one frame both see the state from before either.
    if (inFlight.current) return;
    inFlight.current = true;
    setCreating(true);
    try {
      const answer = await createEsimZone({ ...style(data), countryIds, tadigs });
      if (answer.outcome === EsimZoneSaveOutcome.NetworksRefused) {
        // Untick what OCS refused, so saving again sends only what it accepts.
        const refusedTadigs = new Map(answer.refusedNetworks.map((r) => [r.tadig, r.reason]));
        setRefused(refusedTadigs);
        const refusedIds = networkOptions
          .flatMap((c) => c.networks)
          .filter((n) => n.tadigs.some((t) => refusedTadigs.has(t)))
          .map((n) => n.networkId);
        setUnticked((now) => new Set([...Array.from(now), ...refusedIds]));
      }
      done(answer, "Zona u krijua.");
    } catch {
      toast.error("Krijimi dështoi. Kontrollo listën e zonave para se ta provosh përsëri.");
    } finally {
      inFlight.current = false;
      setCreating(false);
      setPending(null);
    }
  };

  const onSubmit: SubmitHandler<IEsimZoneForm> = async (data) => {
    try {
      setResult(null);
      if (mode === "create") {
        if (!networksReady) {
          toast.error(
            uncovered.length ? "Çdo shtet ka nevojë për të paktën një rrjet." : "Zgjidh shtetet e zonës."
          );
          return;
        }
        setPending(data);
      } else {
        const body = { ...style(data), blockedForPurchase: data.blockedForPurchase };
        const answer = await updateEsimZone(Number(id), body);
        done(answer, "Zona u ruajt.");
      }
    } catch {
      toast.error("Ruajtja dështoi.");
    }
  };

  const existingId = result?.outcome === EsimZoneSaveOutcome.SameNetworksExist ? result.existingZoneId : null;

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim/zones"
        state={CRUMBS}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek zonat</span>
      </Link>
      <FormWrapper
        title={TITLES[mode]}
        history={mode === "edit" ? { entityName: "LocationZone", entityId: id } : undefined}
        showPrompt={!saved && !!Object.keys(dirtyFields).length}
        onSubmit={handleSubmit(onSubmit)}
        contentClassName="max-w-2xl mx-auto"
      >
        <Input
          {...register("nameSq")}
          label="Emri në shqip"
          className="mb-4"
          error={errors.nameSq?.message}
        />
        <Input
          {...register("nameEn")}
          label="Emri në anglisht"
          className="mb-4"
          error={errors.nameEn?.message}
        />
        {mode === "edit" && zone.response?.data?.id && (
          <div className="mb-4">
            <p className="text-neutral mb-1.5 text-sm">
              <WithTip label="Shtetet" tip={FIXED} />
            </p>
            <p className="text-sm">{zone.response.data.countries.join(", ") || "–"}</p>
          </div>
        )}
        {mode === "create" && (
          <>
            <Controller
              control={control}
              name="countries"
              render={({ field: { value, onChange } }) => (
                <Select
                  className="mb-4"
                  label="Shtetet"
                  placeholder="Kërko shtetet"
                  selectType="multiple"
                  selectedItems={value}
                  onSelectedListChange={onChange}
                  getOptionLabel={(o) => o.countryName}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.countryName || ""}
                  asyncFunc={getAllCountries}
                />
              )}
            />
            <NetworkPicker
              options={networkOptions}
              loading={options.loading && countries.length > 0}
              failed={!!options.error}
              unticked={unticked}
              refused={refused}
              onToggleNetwork={(networkId) => setUnticked((now) => toggled(now, networkId))}
            />
          </>
        )}
        <ZoneStyleFields showBlocked={mode === "edit"} />
        {result && result.outcome !== EsimZoneSaveOutcome.Saved && (
          <AlertBox type="warning" className="mt-2" message={result.message} />
        )}
        {existingId !== null && result?.existingZoneIsOurs && (
          <Link className="text-sm underline" to={`/esim/zones/edit/${existingId}`} state={CRUMBS}>
            Hap zonën {existingId}
          </Link>
        )}
      </FormWrapper>
      <Modal show={!!pending} position="center" hasHeader={false} modalWidth="400px" fitContent>
        <div className="p-6 text-center">
          <h1 className="text-lg font-medium mb-3">Krijo zonën në OCS?</h1>
          <p className="text-sm text-neutral mb-6">{CONFIRM}</p>
          <div className="flex gap-4">
            <Button
              btnType="custom"
              fontWeight="medium"
              className="w-full border"
              disabled={creating}
              onClick={() => setPending(null)}
            >
              Anulo
            </Button>
            <Button
              fontWeight="medium"
              className="w-full flex items-center justify-center"
              disabled={creating}
              onClick={() => pending && void create(pending)}
            >
              {creating && <SpinLoader formatClassName="w-4 h-4 mr-2" />}
              Krijo
            </Button>
          </div>
        </div>
      </Modal>
    </FormProvider>
  );
};

export default EsimZoneForm;
